import { Button } from '../../../shared/ui'
import { capForDate, isValidTimeZone, localDateIn, validateRamp } from '../../../lib/outreach/ramp'
import { ErrorLine, parseWhole } from './shared'
import { rampFromDraft } from './programDraft'
import type { RampDraftRow } from './programDraft'

interface Props {
  rows: RampDraftRow[]
  onChange: (rows: RampDraftRow[]) => void
  dailyCap: string
  timezone: string
  error?: string | undefined
}

/** Preview of today's cap, or null when the inputs cannot give one. */
function todaysCap(rows: RampDraftRow[], dailyCap: string, timezone: string): number | null {
  const flat = parseWhole(dailyCap)
  if (Number.isNaN(flat)) return null
  const ramp = rampFromDraft(rows)
  if (validateRamp(ramp)) return null
  const tz = isValidTimeZone(timezone) ? timezone : 'UTC'
  return capForDate(ramp, flat, localDateIn(tz))
}

export function RampEditor({ rows, onChange, dailyCap, timezone, error }: Props) {
  const set = (i: number, patch: Partial<RampDraftRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const cap = todaysCap(rows, dailyCap, timezone)

  return (
    <fieldset className="settings-row os-field os-fieldset">
      <legend className="settings-label">Ramp</legend>
      <div className="settings-hint">
        From each date, that cap applies. Before the first date the daily cap applies.
      </div>
      {rows.map((r, i) => (
        <div className="os-ramp-row" key={i}>
          <label className="settings-label" htmlFor={`ramp-from-${i}`}>
            From date
          </label>
          <input
            id={`ramp-from-${i}`}
            className="settings-input"
            type="date"
            value={r.from}
            onChange={(e) => set(i, { from: e.target.value })}
          />
          <label className="settings-label" htmlFor={`ramp-cap-${i}`}>
            Cap per day
          </label>
          <input
            id={`ramp-cap-${i}`}
            className="settings-input"
            type="number"
            min={0}
            value={r.cap}
            onChange={(e) => set(i, { cap: e.target.value })}
          />
          <Button
            variant="secondary"
            aria-label={`Remove ramp row ${i + 1}`}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
      ))}
      <div className="os-bar">
        <Button variant="secondary" onClick={() => onChange([...rows, { from: '', cap: '' }])}>
          Add ramp row
        </Button>
        {cap !== null && <span className="settings-hint">Today&apos;s cap would be {cap}</span>}
      </div>
      <ErrorLine>{error}</ErrorLine>
    </fieldset>
  )
}
